import { Button, Input, InputNumber, message, Select, Statistic, Table } from "antd";
import { doc, updateDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";
import { FinanceEntryType } from "../typeDefs";
import { GST_REGISTRATION_LABEL, isInGstRegisteredPeriod, requiresGstDetails } from "./taxConfig";

const GST_RATE = 0.15;
const GST_INCLUSIVE_DIVISOR = 23;
const GST_INCLUSIVE_MULTIPLIER = 3;

const money = (value: number) => `$${value.toFixed(2)}`;

type GstResolutionDraft = {
  gstIncluded: boolean;
  gstRegistered: boolean;
  gstNumber: string | null;
  apportionMethod: string | null;
  apportionRate: number;
};

const getGstAmount = (entry: FinanceEntryType) => {
  if (!isInGstRegisteredPeriod(entry.date) || !entry.gstRegistered || !entry.gstIncluded) return 0;

  const baseGst = entry.gstIncluded
    ? (entry.amount * GST_INCLUSIVE_MULTIPLIER) / GST_INCLUSIVE_DIVISOR
    : entry.amount * GST_RATE;

  if (entry.category === "Expenses" || entry.category === "Assets") {
    const apportionRate =
      entry.apportionMethod === "None" ? 0 : entry.apportionRate ?? 100;
    return baseGst * (apportionRate / 100);
  }

  return baseGst;
};

export const FinanceGstReport = ({
  data,
  dateRange,
}: {
  data: FinanceEntryType[];
  dateRange: number[];
}) => {
  const entries = data
    .filter((item) => !item.isDeleted)
    .filter((item) => item.date >= dateRange[0] && item.date < dateRange[1]);

  const gstCollected = entries
    .filter((item) => item.category === "Revenue")
    .reduce((total, item) => total + getGstAmount(item), 0);

  const gstClaimable = entries
    .filter((item) => item.category === "Expenses" || item.category === "Assets")
    .reduce((total, item) => total + getGstAmount(item), 0);

  const netGst = gstCollected - gstClaimable;

  const missingGstDetails = entries.filter(
    (item) =>
      requiresGstDetails(item) &&
      (typeof item.gstIncluded !== "boolean" ||
        typeof item.gstRegistered !== "boolean" ||
        ((item.category === "Expenses" || item.category === "Assets") &&
          item.gstIncluded === true &&
          !item.apportionMethod)),
  );

  const [drafts, setDrafts] = useState<Record<string, GstResolutionDraft>>({});

  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      let changed = false;

      missingGstDetails.forEach((item) => {
        if (!next[item.id]) {
          changed = true;
          next[item.id] = {
            gstIncluded: typeof item.gstIncluded === "boolean" ? item.gstIncluded : true,
            gstRegistered:
              typeof item.gstRegistered === "boolean" ? item.gstRegistered : true,
            gstNumber: item.gstNumber || null,
            apportionMethod:
              item.apportionMethod ||
              (item.category === "Expenses" || item.category === "Assets"
                ? "Principal Method"
                : null),
            apportionRate: item.apportionRate ?? 100,
          };
        }
      });

      return changed ? next : prev;
    });
  }, [missingGstDetails]);

  const updateDraft = (id: string, patch: Partial<GstResolutionDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...patch,
      },
    }));
  };

  const saveGstResolution = async (entry: FinanceEntryType) => {
    const draft = drafts[entry.id];

    if (!draft) return;

    try {
      await updateDoc(doc(db, `finances/${entry.id}`), {
        gstIncluded: draft.gstIncluded,
        gstRegistered: draft.gstRegistered,
        gstNumber: draft.gstNumber || null,
        apportionMethod:
          entry.category === "Expenses" || entry.category === "Assets"
            ? draft.apportionMethod
            : null,
        apportionRate:
          entry.category === "Expenses" || entry.category === "Assets"
            ? draft.apportionMethod === "None"
              ? 0
              : draft.apportionRate
            : 100,
      } as Partial<FinanceEntryType>);

      message.success("GST details resolved.");
    } catch (err) {
      console.error(err);
      message.error("Could not save GST details.");
    }
  };

  const saveAllGstResolutions = async () => {
    try {
      await Promise.all(
        missingGstDetails.map((entry) => {
          const draft = drafts[entry.id];
          if (!draft) return Promise.resolve();

          return updateDoc(doc(db, `finances/${entry.id}`), {
            gstIncluded: draft.gstIncluded,
            gstRegistered: draft.gstRegistered,
            gstNumber: draft.gstNumber || null,
            apportionMethod:
              entry.category === "Expenses" || entry.category === "Assets"
                ? draft.apportionMethod
                : null,
            apportionRate:
              entry.category === "Expenses" || entry.category === "Assets"
                ? draft.apportionMethod === "None"
                  ? 0
                  : draft.apportionRate
                : 100,
          } as Partial<FinanceEntryType>);
        }),
      );

      message.success("All GST resolutions saved.");
    } catch (err) {
      console.error(err);
      message.error("Could not save all GST details.");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        <Statistic title="GST collected on revenue" value={gstCollected} precision={2} prefix="$" />
        <Statistic title="GST claimable" value={gstClaimable} precision={2} prefix="$" />
        <Statistic
          title={netGst >= 0 ? "GST payable" : "GST refundable"}
          value={Math.abs(netGst)}
          precision={2}
          prefix="$"
          valueStyle={{ color: netGst >= 0 ? "#cf1322" : "#3f8600" }}
        />
      </div>

      <p style={{ margin: 0 }}>
        GST is only calculated for entries dated on or after the company GST registration date: {GST_REGISTRATION_LABEL}.
        Earlier entries do not need GST details and are excluded from GST payable/refundable totals.
      </p>

      <Button onClick={() => window.print()}>Print GST Report</Button>

      <Table
        size="small"
        pagination={{ pageSize: 10 }}
        dataSource={entries.map((item) => ({ ...item, key: item.id }))}
        columns={[
          {
            title: "Date",
            dataIndex: "date",
            render: (value) => new Date(value).toLocaleDateString("en-GB"),
          },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Category", dataIndex: "category" },
          { title: "Subcategory", dataIndex: "subCategory" },
          { title: "Amount", dataIndex: "amount", render: money },
          { title: "GST", render: (_value, record) => money(getGstAmount(record)) },
          {
            title: "Apportionment",
            render: (_value, record) =>
              record.category === "Expenses" || record.category === "Assets"
                ? `${record.apportionMethod || "Principal Method"} ${record.apportionRate ?? 100}%`
                : "-",
          },
        ]}
      />

      <Table
        title={() => (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span>Resolve entries missing GST details from {GST_REGISTRATION_LABEL}</span>
            <Button
              size="small"
              type="primary"
              disabled={!missingGstDetails.length}
              onClick={saveAllGstResolutions}
            >
              Save All Resolutions
            </Button>
          </div>
        )}
        size="small"
        pagination={{ pageSize: 8 }}
        dataSource={missingGstDetails.map((item) => ({ ...item, key: item.id }))}
        columns={[
          {
            title: "Date",
            dataIndex: "date",
            render: (value) => new Date(value).toLocaleDateString("en-GB"),
          },
          { title: "Vendor", dataIndex: "vendor" },
          { title: "Description", dataIndex: "description" },
          { title: "Amount", dataIndex: "amount", render: money },
          {
            title: "GST Included",
            render: (_value, record) => (
              <Select
                value={drafts[record.id]?.gstIncluded}
                style={{ width: 105 }}
                onChange={(value) => updateDraft(record.id, { gstIncluded: value })}
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
              />
            ),
          },
          {
            title: "GST Registered",
            render: (_value, record) => (
              <Select
                value={drafts[record.id]?.gstRegistered}
                style={{ width: 105 }}
                onChange={(value) => updateDraft(record.id, { gstRegistered: value })}
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
              />
            ),
          },
          {
            title: "GST Number",
            render: (_value, record) => (
              <Input
                value={drafts[record.id]?.gstNumber || ""}
                placeholder="Optional"
                onChange={(event) =>
                  updateDraft(record.id, { gstNumber: event.target.value || null })
                }
              />
            ),
          },
          {
            title: "Apportionment",
            render: (_value, record) =>
              record.category === "Expenses" || record.category === "Assets" ? (
                <Select
                  value={drafts[record.id]?.apportionMethod}
                  style={{ width: 180 }}
                  onChange={(value) => updateDraft(record.id, { apportionMethod: value })}
                  options={[
                    { value: "Principal Method", label: "Principal Method" },
                    { value: "Home Office Business Use", label: "Home Office Business Use" },
                    { value: "Apportionment Method", label: "Apportionment Method" },
                    { value: "None", label: "None" },
                  ]}
                />
              ) : (
                "-"
              ),
          },
          {
            title: "Claim %",
            render: (_value, record) =>
              record.category === "Expenses" || record.category === "Assets" ? (
                <InputNumber
                  min={0}
                  max={100}
                  value={drafts[record.id]?.apportionRate}
                  disabled={drafts[record.id]?.apportionMethod === "None"}
                  onChange={(value) =>
                    updateDraft(record.id, { apportionRate: Number(value || 0) })
                  }
                />
              ) : (
                "-"
              ),
          },
          {
            title: "Action",
            render: (_value, record) => (
              <Button size="small" onClick={() => saveGstResolution(record)}>
                Save
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
};
